import { useMemo } from 'react'

import { useCurrentUser } from './useCurrentUser'

/**
 * UID do usuário da sessão, ou `undefined` quando não há sessão, ainda carrega, ou o user não tem uid.
 * Evita encadear optional chaining em `useCurrentUser().currentUser` em cada tela.
 */
export function useCurrentUserUid(): string | undefined {
  const { currentUser } = useCurrentUser()
  return currentUser?.uid
}

/**
 * UID para props que exigem string (ex.: analytics). Usa `fallback` quando não há viewer.
 */
export function useCurrentUserUidOr(fallback = ''): string {
  return useCurrentUserUid() ?? fallback
}

/**
 * Compara o uid do viewer com outro uid de forma segura (nunca assume viewer definido).
 */
export function viewerMatchesUid(
  viewerUid: string | undefined,
  otherUid: string | undefined | null
): boolean {
  if (viewerUid === undefined || viewerUid === '') return false
  if (otherUid === undefined || otherUid === null || otherUid === '') return false
  return viewerUid === otherUid
}

/**
 * Indica se o usuário logado é o mesmo que `otherUid` (ex.: autor de um item do feed).
 * Retorna `false` quando não autenticado, sessão pendente ou uids diferentes.
 */
export function useIsCurrentViewer(otherUid: string | undefined | null): boolean {
  const viewerUid = useCurrentUserUid()
  return useMemo(
    () => viewerMatchesUid(viewerUid, otherUid),
    [viewerUid, otherUid]
  )
}
